/**
 * Ranked assignee suggestions shown above the manual assignee picker. The manager asks for
 * suggestions, reads short number-free reasons, and chooses; nothing is assigned until the
 * activity is created. Only managers see this panel, and the API enforces that.
 *
 * @module apps/web/src/components/teams/AssigneeSuggestions
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ACTIVITY_KINDS,
  TEAM_SCHEDULING_NOTE,
  draftWindow,
  numberFreeReasons,
  teamSchedulingApi,
  type ActivityKindValue,
  type AssigneeSuggestion,
} from '@/lib/teamScheduling';

/** Inputs for the suggestion panel. */
export interface AssigneeSuggestionsProps {
  /** Team whose members are ranked; the server checks the caller is a manager. */
  teamId: string;
  /** Current activity title; suggestions need one. */
  title: string;
  /** Current notes, sent so a future classifier can infer the kind of work. */
  description: string;
  /** Duration in minutes as typed; ignored when not a positive number. */
  durationMinutes: string;
  /** Local due date from the form; bounds the window when it is within 31 days. */
  dueDate: string;
  /** The currently chosen assignee's user id, highlighted in the list. */
  selectedUserId: string;
  /** Called with a user id when the manager picks a suggestion. */
  onChoose: (userId: string) => void;
}

/** Ask for and show ranked assignee suggestions; the manager chooses one or ignores them. */
export function AssigneeSuggestions({
  teamId,
  title,
  description,
  durationMinutes,
  dueDate,
  selectedUserId,
  onChoose,
}: AssigneeSuggestionsProps) {
  const [kind, setKind] = useState<ActivityKindValue | ''>('');
  const [suggestions, setSuggestions] = useState<AssigneeSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /** Request suggestions for the current draft. */
  const suggest = async () => {
    if (!title.trim()) {
      setError('Add a title first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const minutes = Number(durationMinutes);
      const result = await teamSchedulingApi.suggestAssignees(teamId, {
        title: title.trim(),
        description: description.trim() || null,
        ...(Number.isFinite(minutes) && minutes >= 5 && minutes <= 1440
          ? { duration_minutes: Math.round(minutes) }
          : {}),
        ...draftWindow(dueDate),
        ...(kind ? { kind } : {}),
      });
      setSuggestions(result.suggestions);
    } catch (cause) {
      setSuggestions(null);
      setError(cause instanceof Error ? cause.message : 'Suggestions are unavailable right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      aria-labelledby="assignee-suggestions-title"
      className="space-y-3 rounded-md border bg-muted/20 p-3"
      data-testid="assignee-suggestions"
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <h3 id="assignee-suggestions-title" className="text-sm font-medium">
            Suggested assignees
          </h3>
          <p className="text-xs text-muted-foreground">{TEAM_SCHEDULING_NOTE}</p>
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium">
          Kind of work
          <select
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={kind}
            onChange={event => setKind(event.target.value as ActivityKindValue | '')}
          >
            <option value="">Not sure</option>
            {ACTIVITY_KINDS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <Button type="button" variant="outline" size="sm" onClick={suggest} disabled={loading}>
          <Sparkles className="mr-1 h-4 w-4" aria-hidden="true" />
          {loading ? 'Ranking…' : suggestions ? 'Refresh suggestions' : 'Suggest assignees'}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {suggestions && suggestions.length === 0 && (
        <p className="text-sm text-muted-foreground">No active members to suggest.</p>
      )}
      {suggestions && suggestions.length > 0 && (
        <ol className="divide-y rounded-md border bg-background" aria-label="Suggested assignees, best first">
          {suggestions.map(suggestion => (
            <SuggestionRow
              key={suggestion.member_id}
              suggestion={suggestion}
              selected={suggestion.user_id === selectedUserId}
              onChoose={onChoose}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

/** One ranked member with reasons and a choose button.
 * @component SuggestionRow
 */
function SuggestionRow({
  suggestion,
  selected,
  onChoose,
}: {
  /** The ranked member. */
  suggestion: AssigneeSuggestion;
  /** Whether this member is the current choice. */
  selected: boolean;
  /** Called with the member's user id. */
  onChoose: (userId: string) => void;
}) {
  const reasons = numberFreeReasons(suggestion.reason_texts);
  return (
    <li
      className={cn(
        'flex flex-col gap-2 p-3 text-sm sm:flex-row sm:items-center sm:justify-between',
        selected && 'bg-primary/5'
      )}
      data-testid="assignee-suggestion"
    >
      <div className="min-w-0">
        <p className={cn('truncate font-medium', !suggestion.available && 'text-muted-foreground')}>
          {suggestion.display_name}
        </p>
        <p className="text-xs text-muted-foreground">{reasons.join(' · ')}</p>
      </div>
      <Button
        type="button"
        size="sm"
        variant={selected ? 'default' : 'outline'}
        aria-pressed={selected}
        onClick={() => onChoose(suggestion.user_id)}
      >
        {selected ? 'Chosen' : 'Choose'}
      </Button>
    </li>
  );
}
